import { useCallback } from 'react';

import {
  CASH_ACCOUNT_IDENTIFIER,
  PAYPAL_ACCOUNT_IDENTIFIER,
  VENMO_ACCOUNT_IDENTIFIER,
} from 'features/payment/constants';
import { IPaymentDetails } from 'features/payment/types';
import {
  getFraudPreventionValues,
  mapPaymentMethodCardTypes,
  splitExpiry,
  transformPaymentValues,
} from 'features/payment/utils';

import { CurrentSelectedMethodType } from 'components/payment-method/types';
import { CartPaymentCardType } from 'generated/graphql-gateway';
import { ICommitOrderInput, IPayment } from 'hooks/commit-order/types';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import {
  PaymentFieldVariations,
  defaultPaymentFieldVariation,
} from 'state/launchdarkly/variations';
import { useServiceModeContext } from 'state/service-mode';
import { logger } from 'utils/logger';

import { useFirstDataPrepaidReload } from '../../../payment/hooks/use-first-data-prepaid-reload';
import { useCheckoutContext } from '../use-checkout-context';
export const useBuildCommitOrderPayload = () => {
  const paymentFieldVariations =
    useFlag<PaymentFieldVariations>(LaunchDarklyFlag.PAYMENT_FIELD_VARIATIONS) ||
    defaultPaymentFieldVariation;
  const { isDelivery } = useServiceModeContext();

  const {
    checkoutPaymentMethod,
    serverOrder,
    billingCountry,
    orbitalEncryptionResult,
    paymentValues,
    isPaymentProcessor,
    isPaymentType,
    checkoutPaymentMethodId,
    reloadAmount,
    storeEmail,
    isOneTimePaymentFlowEnabled,
    tipAmount,
  } = useCheckoutContext();

  const { reloadPrepaid } = useFirstDataPrepaidReload({
    serverOrder,
    reloadAmount,
    billingCountry,
  });

  const fraudPreventionValues = paymentValues ? getFraudPreventionValues(paymentValues) : undefined;
  const reshapedPaymentValues = transformPaymentValues({
    paymentValues,
    paymentFieldVariations,
  });
  const fullName = fraudPreventionValues?.fullName ?? '';
  const accountIdentifier =
    checkoutPaymentMethod?.accountIdentifier ?? checkoutPaymentMethod?.fdAccountId ?? '';

  const deliveryTipCents = isDelivery ? tipAmount : undefined;

  const buildOrbitalPayload = useCallback((): ICommitOrderInput | undefined => {
    if (!fraudPreventionValues || !paymentValues || !reshapedPaymentValues) {
      logger.error({
        message:
          '[useBuildCommitOrderPayload][buildOrbitalPayload]: Missing required fraudPreventionValues, paymentValues, or reshapedPaymentValues.',
        fraudPreventionValues,
        paymentValues,
        reshapedPaymentValues,
      });
      return;
    }
    const { expiryMonth, expiryYear } = splitExpiry(fraudPreventionValues.expiry ?? '');

    if (!orbitalEncryptionResult || !('cryptCard' in orbitalEncryptionResult)) {
      logger.error({
        message:
          '[useBuildCommitOrderPayload][buildOrbitalPayload]: Missing or invalid orbitalEncryptionResult. Expected property "cryptCard" to be present.',
        orbitalEncryptionResult,
      });
      return;
    }
    const {
      cryptCard,
      cryptCvv,
      integrityCheck: pieIntegrityCheck,
      pieFormat,
      subscriberId: pieSubscriberId,
      mode: pieMode,
      keyId: pieKeyID,
      phase: piePhaseId,
    } = orbitalEncryptionResult;

    if (accountIdentifier && accountIdentifier !== CurrentSelectedMethodType.ADD_NEW_CARD) {
      return {
        creditType: paymentValues.cardType,
        // order,
        payment: {
          billingAddress: reshapedPaymentValues.billingAddress,
          ccMetadata: {
            ccBin: fraudPreventionValues.ccBin,
            ccExpiryMonth: expiryMonth,
            ccExpiryYear: expiryYear,
            ccLast4: fraudPreventionValues.ccLast4,
          },
          orbitalInput: {
            accountIdentifier: checkoutPaymentMethod?.accountIdentifier,
            savePaymentMethod: false,
          },
          fullName: fraudPreventionValues.fullName,
        },
        skipCoolingPeriod: false,
      };
    }

    // One Time Payment
    return {
      creditType: paymentValues.cardType,
      // order,
      payment: {
        billingAddress: fraudPreventionValues.billingAddress,
        ccMetadata: {
          ccBin: fraudPreventionValues.ccBin,
          ccExpiryMonth: expiryMonth,
          ccExpiryYear: expiryYear,
          ccLast4: fraudPreventionValues.ccLast4,
        },
        orbitalInput: {
          encryptedPayload: {
            encryptedCardNum: cryptCard,
            encryptedCvv: cryptCvv,
            expiryMonth: reshapedPaymentValues.expiryDate?.month!,
            expiryYear: reshapedPaymentValues.expiryDate?.year!,
            pieFormat,
            pieMode,
            piePhaseId: `${piePhaseId}`,
            pieIntegrityCheck,
            pieSubscriberId,
            pieKeyID,
            cardBrand: paymentValues.cardType ? paymentValues.cardType.toLowerCase() : '',
            bin: reshapedPaymentValues.cardNumber.slice(0, 6),
          },
          savePaymentMethod: isOneTimePaymentFlowEnabled ? paymentValues.saveCard : false,
        },
        fullName: fraudPreventionValues.fullName,
      },
      skipCoolingPeriod: false,
    };
  }, [
    accountIdentifier,
    checkoutPaymentMethod?.accountIdentifier,
    fraudPreventionValues,
    isOneTimePaymentFlowEnabled,
    orbitalEncryptionResult,
    paymentValues,
    reshapedPaymentValues,
  ]);

  const buildFirstDataPayload = useCallback(
    (paymentDetails: IPaymentDetails | undefined): ICommitOrderInput | undefined => {
      if (!fraudPreventionValues || !paymentValues || !reshapedPaymentValues) {
        logger.error({
          message:
            '[useBuildCommitOrderPayload][buildFirstDataPayload]: Missing required fraudPreventionValues, paymentValues, or reshapedPaymentValues.',
          fraudPreventionValues,
          paymentValues,
          reshapedPaymentValues,
        });
        return;
      }
      const { expiryMonth, expiryYear } = splitExpiry(fraudPreventionValues.expiry ?? '');
      return {
        creditType: paymentValues.cardType,
        payment: {
          billingAddress: reshapedPaymentValues.billingAddress,
          ccMetadata: {
            ccBin: fraudPreventionValues.ccBin,
            ccExpiryMonth: expiryMonth,
            ccExpiryYear: expiryYear,
            ccLast4: fraudPreventionValues.ccLast4,
          },
          firstDataInput: {
            accountIdentifier: undefined,
            fdAccessToken: paymentDetails?.fdAccessToken,
            fdNonce: paymentDetails?.fdNonce,
            storePaymentMethod: paymentValues.saveCard,
          },
          fullName: fraudPreventionValues.fullName,
        },
        skipCoolingPeriod: false,
      };
    },
    [fraudPreventionValues, paymentValues, reshapedPaymentValues]
  );

  const buildCommitOrderPayloadWithoutAccount = useCallback(
    async (paymentDetails: IPaymentDetails | undefined): Promise<ICommitOrderInput | undefined> => {
      if (isPaymentType.prepaid && reloadAmount > 0) {
        await reloadPrepaid();
      }

      const creditType = mapPaymentMethodCardTypes(checkoutPaymentMethod?.credit?.cardType);

      const commitInput = {
        creditType,
        storeEmail,
        deliveryTipCents,
      };

      if (isPaymentType.googlePay || isPaymentType.applePay) {
        const digitalPayDetails = {
          applePayDetails: paymentDetails?.applePayDetails,
          googlePayDetails: paymentDetails?.googlePayDetails,
          fullName,
        };

        return {
          ...commitInput,
          payment: {
            ...digitalPayDetails,
          },
        };
      }

      if (isPaymentProcessor.braintree) {
        return {
          ...commitInput,
          creditType: isPaymentType.paypal ? PAYPAL_ACCOUNT_IDENTIFIER : VENMO_ACCOUNT_IDENTIFIER,
          payment: {
            fullName,
            paypalDetails: paymentDetails?.paypalDetails,
            venmoDetails: paymentDetails?.venmoDetails,
          },
          skipCoolingPeriod: false,
        };
      }

      if (isPaymentType.cashApp) {
        return {
          ...commitInput,
          creditType: CartPaymentCardType.CASHAPP,
          payment: {
            fullName: '',
            cashAppInput: {
              fdAccessToken: paymentDetails?.cashappDetails?.fdAccessToken ?? '',
              fdNonce: paymentDetails?.cashappDetails?.fdNonce ?? '',
            },
            ccMetadata: {
              ccBin: paymentDetails?.cashappDetails?.virtualCard.cardNumber.slice(0, 6),
              ccExpiryMonth: paymentDetails?.cashappDetails?.virtualCard.expiryMonth,
              ccExpiryYear: paymentDetails?.cashappDetails?.virtualCard.expiryYear,
              ccLast4: paymentDetails?.cashappDetails?.virtualCard.cardNumber.slice(-4),
            },
          },
          skipCoolingPeriod: false,
        };
      }

      if (isPaymentProcessor.firstData) {
        return buildFirstDataPayload(paymentDetails);
      }

      if (isPaymentProcessor.orbital) {
        return buildOrbitalPayload();
      }

      return;
    },
    [
      buildFirstDataPayload,
      buildOrbitalPayload,
      checkoutPaymentMethod?.credit?.cardType,
      deliveryTipCents,
      fullName,
      isPaymentProcessor.braintree,
      isPaymentProcessor.firstData,
      isPaymentProcessor.orbital,
      isPaymentType.applePay,
      isPaymentType.cashApp,
      isPaymentType.googlePay,
      isPaymentType.paypal,
      isPaymentType.prepaid,
      reloadAmount,
      reloadPrepaid,
      storeEmail,
    ]
  );

  const buildCommitOrderPayloadWithAccount = useCallback(async (): Promise<
    ICommitOrderInput | undefined
  > => {
    if (isPaymentType.prepaid && reloadAmount > 0) {
      await reloadPrepaid();
    }

    const creditType = mapPaymentMethodCardTypes(checkoutPaymentMethod?.credit?.cardType);

    const commitInput: ICommitOrderInput = {
      creditType,
      storeEmail,
      deliveryTipCents,
    };

    if (checkoutPaymentMethodId === CASH_ACCOUNT_IDENTIFIER) {
      const paymentData: IPayment = {
        cashPayment: true,
        fullName,
      };
      return {
        ...commitInput,
        creditType: CASH_ACCOUNT_IDENTIFIER,
        payment: paymentData,
      };
    }

    if (isPaymentProcessor.orbital) {
      return {
        ...commitInput,
        payment: {
          orbitalInput: {
            accountIdentifier,
          },
          fullName,
        },
      };
    }
    if (isPaymentProcessor.firstData) {
      return {
        ...commitInput,
        payment: {
          firstDataInput: {
            accountIdentifier,
          },
          fullName,
        },
      };
    }
    return;
  }, [
    accountIdentifier,
    checkoutPaymentMethod?.credit?.cardType,
    checkoutPaymentMethodId,
    deliveryTipCents,
    fullName,
    isPaymentProcessor.firstData,
    isPaymentProcessor.orbital,
    isPaymentType.prepaid,
    reloadAmount,
    reloadPrepaid,
    storeEmail,
  ]);

  return { buildCommitOrderPayloadWithoutAccount, buildCommitOrderPayloadWithAccount };
};
